import { SearchOutlined } from "@ant-design/icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Col, Form, Row } from "antd";
import { isEmpty, isNull } from "lodash";
import React, { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  clearListCompactAction,
  listAgenciesCompactAction,
} from "../../../store/agency/agencyActions";
import {
  listDocumentsAction,
  setRefreshListFlagAction,
  setSearchValuesAction,
} from "../../../store/document/documentActions";
import useUserPreferences from "../../../hooks/useUserPreferences";
import { setTablePreserveOnReload } from "../../../store/ui/uiActions";
import AgencyMultiSelectEx from "../../shared/form/AgencyMultiSelectEx";
import SearchSelectEx from "../../shared/form/SearchSelectEx";
import { shortLabelLongInputLayout as layout } from "../../shared/form/formLayout";
import { getInitialAgency } from "../../shared/form/getInitialAgency";
import CustomInput from "../../shared/form/reactHookForm/CustomInput";
import { ViewDocuments } from "../../shared/securityRoleTasks";
import { agenciesListEqual } from "../../shared/sorting";
import SearchTip from "./SearchTip";
import searchByColumns from "./searchByColumns";
import searchFormSchema from "./searchFormSchema";
import { tableNames } from "../../shared/table/tableNames";
import { dateRangeConfigs, fixedDateRanges } from "../../shared/form/fixedDateRanges";
import { dateFormat } from "../../shared/formatting";
import dayjs from "dayjs" ;
import AdvancedRangePicker from "../../shared/form/reactHookForm/AdvancedRangePicker";

const initialValues = {
  SearchValue: "",
  Column: "Autodetect",
  AgencyKeys: [], // by default select all agencies
  FixedDateRange: fixedDateRanges.ALL_DATES,
  TransactionDate: [null, null]
};

const rightColumnLayout = {
  labelCol: { span: 6, lg: 8 },
  wrapperCol: { span: 18, lg: 16 },
};

const SearchForm = ({
  onSubmitHandler,
  onResetHandler,
  loading,
  setClearAll,
}) => {
  const searchValues = useSelector((state) => state.document.searchValues);
  const refreshListFlag = useSelector(
    (state) => state.document.refreshListFlag
  );

  // Form
  const {
    handleSubmit,
    reset,
    control,
    setValue,
    trigger,
    formState: { isValid, defaultValues },
  } = useForm({
    mode: "onChange",
    defaultValues: searchValues != null ? searchValues : initialValues,
    resolver: yupResolver(searchFormSchema),
  });

  //
  const dispatch = useDispatch();

  //
  const [currentAgencies, setCurrentAgencies] = useState([]);

  //
  const brokerId = useSelector((state) => state.actor.details.data.BrokerId);
  const agencies = useSelector((state) => state.agency.listCompact);
  const homeAgency = useSelector((state) => state.actor.details.data.AgencyKey);
  const selectedAgencies = useWatch({
    control,
    name: "AgencyKeys"
  });
  const {
    preferences,
    saveUserPreferences,
  } = useUserPreferences(["DocumentSearchDefaultAgencyKeys"]);
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [savingAgencySelection, setSavingAgencySelection] = useState(false);

  const savedAgencySelection = preferences?.DocumentSearchDefaultAgencyKeys;

  useEffect(() => {
    if (brokerId != null) {
      dispatch(listAgenciesCompactAction(brokerId, ViewDocuments));
    }
  }, [brokerId]);

  useEffect(() => {
    setShowSaveButton(false);
    if (!isValidAgencyList(agencies)) return;

    // Set the default agency selection from saved user preference
    setValue("AgencyKeys", getSavedAgencySelection(), { shouldValidate: true });
  }, [savedAgencySelection, agencies]);

  // Track if user selection differs from saved selection
  useEffect(() => {
    if (!isValidAgencyList(agencies)) return;

    const loadedSelection = getSavedAgencySelection();
    const selectedAgenciesAsString = JSON.stringify(selectedAgencies?.sort() || []);
    const loadedSelectionAsString = JSON.stringify(loadedSelection?.sort() || []);
    const isNonEmptySelection = selectedAgencies?.length > 0;
    const isDifferent = selectedAgenciesAsString !== loadedSelectionAsString;
    setShowSaveButton(isNonEmptySelection && isDifferent);
  }, [selectedAgencies]);

  useEffect(() => {
    if (
      isValidAgencyList(agencies) &&
      !agenciesListEqual(currentAgencies, agencies)
    ) {
      setCurrentAgencies(agencies);
      if (searchValues == null) {
        resetFieldsToDefault();
      } else {
        reset({ ...searchValues });
      }
    }
  }, [agencies, homeAgency]);

  useEffect(() => {
    // Refresh search results
    if (refreshListFlag === true) {
      if (!isNull(searchValues)) {
        dispatch(setTablePreserveOnReload(tableNames.DocumentSearch, true));
        dispatch(listDocumentsAction(searchValues));
      }
      dispatch(setRefreshListFlagAction(false));
    }
  }, [refreshListFlag]);

  useEffect(() => {
    return () => {
      // Clear the list of all agencies
      dispatch(clearListCompactAction());
    };
  }, []);

  const isValidAgencyList = (agencies) => {
    return Array.isArray(agencies) &&
      agencies.length > 0 &&
      agencies.every(a => a && typeof a === "object" && "Id" in a);
  };

  const getSavedAgencySelection = () => {
    if (!isValidAgencyList(agencies)) {
      return [];
    }

    const agencyIds = agencies.map(a => a.Id);

    if (savedAgencySelection === "all") {
      return agencyIds; // Return all agency IDs
    }

    const initialAgency = getInitialAgency(homeAgency, agencies);

    if (!savedAgencySelection) {
      return [initialAgency]; // Default to home agency
    }

    const savedAgencyIds = typeof savedAgencySelection === "string"
      ? savedAgencySelection
        .split(",")
        .map(id => parseInt(id.trim(), 10))
        .filter(id => !isNaN(id) && agencyIds.includes(id))
      : []; // Only keep valid IDs

    return savedAgencyIds.length > 0 ? savedAgencyIds : [initialAgency];
  };

  const handleSaveAgencySelection = async () => {
    setSavingAgencySelection(true);
    try {
      saveUserPreferences(
        { DocumentSearchDefaultAgencyKeys: selectedAgencies },
        brokerId
      );
    } finally {
      setSavingAgencySelection(false);
    }
  };

  const resetFieldsToDefault = () => {
    reset({
      ...initialValues,
      AgencyKeys: getSavedAgencySelection(),
    });
    trigger();
    dispatch(setSearchValuesAction(null));
  };

  const onSubmitHandlerEx = (values) => {
    // Save search criteria
    dispatch(setSearchValuesAction(values));
    onSubmitHandler(values);
    reset(values);
  };

  return (
    <div>
      <Row>
        <Col span={24}>
          <Form
            size="small"
            autoComplete="off"
            onFinish={() => handleSubmit(onSubmitHandlerEx)()}
          >
            <Row>
              <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={10}>
                <SearchSelectEx
                  control={control}
                  layout={layout}
                  formLabel="Search By"
                  required={true}
                  name="Column"
                  options={searchByColumns}
                  allowClear={true}
                  maxTagCount={4}
                  afterChange={(e) => {
                    // if selection is getting cleared
                    if (isNull(e) || e == undefined) {
                      setValue("Column", "Autodetect", {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }
                  }}
                />
                <AgencyMultiSelectEx
                  formStyle={{ marginBottom: 0 }}
                  layout={layout}
                  formLabel="Agency"
                  required={true}
                  agencies={currentAgencies}
                  isLoading={isEmpty(defaultValues.AgencyKeys)}
                  setValue={setValue}
                  control={control}
                  name="AgencyKeys"
                  InfoForMultiSelectParentStyle={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                  }}
                  InfoForMultiSelectExtraContent={
                    showSaveButton && (
                      <div>
                        {!savingAgencySelection ? (
                          <a
                            onClick={handleSaveAgencySelection}
                            style={{ cursor: "pointer", color: "#1890ff" }}
                          >
                            Save selection for future searches
                          </a>
                          ) : (
                          <span style={{ color: "#888" }}>
                            Saving selection for future searches...
                          </span>
                        )}
                      </div>
                    )
                  }
                />
              </Col>
              <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={10}>
                <CustomInput
                  layout={rightColumnLayout}
                  control={control}
                  formLabel="Search For"
                  formTooltip={SearchTip}
                  required={true}
                  name="SearchValue"
                  placeholder="Enter search criteria, for more details click on help button"
                  autoFocus={true}
                />

                <AdvancedRangePicker
                  formItem={{
                    layout: rightColumnLayout,
                    required: true,
                    label: "Transaction Date",
                  }}
                  control={control}
                  allowClear={true}
                  name="TransactionDate"
                  format={dateFormat}
                  dateRangeConfig={dateRangeConfigs.NON_TRANSACTION_NO_FUTURE_WITH_ALL}
                  setValue={setValue}
                  disabledDate={(current) =>
                    current && current > dayjs().endOf("day")
                  }
                />
                <Row>
                  <Col xs={0} sm={0} md={0} lg={24} xl={24} xxl={0}>
                    <SearchButtons
                      onClick={() => {
                        resetFieldsToDefault();
                        onResetHandler();
                        setClearAll(true);
                      }}
                      isValid={isValid}
                      loading={loading}
                    />
                  </Col>
                </Row>
              </Col>

              <Col xs={24} sm={24} md={24} lg={0} xl={0} xxl={4}>
                <SearchButtons
                  onClick={() => {
                    resetFieldsToDefault();
                    onResetHandler();
                    setClearAll(true);
                  }}
                  isValid={isValid}
                  loading={loading}
                />
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

const SearchButtons = (props) => {
  const { isValid, loading, onClick } = props;
  return (
    <Row justify="center" gutter={8}>
      <Col>
        <Button
          disabled={!isValid}
          loading={loading}
          autoFocus={true}
          id="processButton"
          type="primary"
          htmlType="submit"
        >
          <SearchOutlined />
          Search
        </Button>
      </Col>
      <Col>
        <Button onClick={onClick}>Clear Results</Button>
      </Col>
    </Row>
  );
};
export default SearchForm;
